import moment from 'moment';
import { fieldColors as sharedFieldColors } from 'shared/common/charts/constants';
import { ActivityMetricType } from 'shared/models/activity-metric';
import { MetricsData } from '../graphql/queries/metrics';
import { chartDate } from './timestamp';

export function getChartHeight(count: number) {
    // tslint:disable:no-magic-numbers
    const minPad = 80;
    if (count < 10) {
        return count * 50 + minPad;
    } else if (count < 16) {
        return count * 45 + minPad;
    } else if (count < 24) {
        return count * 40 + minPad;
    } else {
        return count * 40 + minPad;
    }
    // tslint:enable:no-magic-numbers
}

interface AllFields {
    search_create: 1;
    manual_sourcing: 1;
    awaiting_am_approval: 1;
    awaiting_client_feedback: 1;
    rocket_screen_complete: 1;
    being_scheduled: 1;
    email_send_candidate: 1;
    email_send_client: 1;
    outreach: 1;
    response_received: 1;
    initial_phone_call: 1;
    follow_up_phone_call: 1;
    text_message: 1;
    client_first_round: 1;
    client_second_round: 1;
    offer: 1;
    hired: 1;
}

export type Field = ActivityMetricType;

export type ClientFieldMetrics = Partial<{
    [Property in keyof AllFields]: {
        value: number;
        jobs: Array<{
            id: string;
            name: string;
            value: number;
        }>;
    };
}>;

export interface ClientMetrics {
    clientId: string;
    client: string;
    metrics: ClientFieldMetrics;
}

type FunnelCounts = Partial<{ [Property in keyof AllFields]: number }>;

export type FieldsSelected = {
    [Property in keyof AllFields]: boolean;
};

export const clientActivityFields: Field[] = [
    'search_create',
    'manual_sourcing',
    'response_received',
    'rocket_screen_complete',
    'awaiting_am_approval',
    'awaiting_client_feedback',
    'client_first_round',
    'client_second_round',
    'offer',
    'hired'
];

export const funnelViewMetrics: Field[] = [
    'search_create',
    'manual_sourcing',
    'outreach',
    'response_received',
    'initial_phone_call',
    'rocket_screen_complete',
    'follow_up_phone_call',
    'text_message',
    'email_send_candidate',
    'email_send_client',
    'awaiting_am_approval',
    'awaiting_client_feedback',
    'client_first_round',
    'client_second_round',
    'offer',
    'hired'
];

export const funnelMetricFields: Field[] = [
    'manual_sourcing',
    'outreach',
    'response_received',
    'rocket_screen_complete',
    'awaiting_am_approval',
    'awaiting_client_feedback',
    'client_first_round',
    'client_second_round',
    'offer',
    'hired'
];

export const defaultAggregateViewSelected: FieldsSelected = clientActivityFields.reduce(
    (acc, f) => ({ ...acc, [f]: true }),
    {}
) as FieldsSelected;

export const defaultFunnelSelected: FieldsSelected = funnelMetricFields.reduce(
    (acc, f) => ({ ...acc, [f]: true }),
    {}
) as FieldsSelected;

export const fieldColors: Map<Field, string> = sharedFieldColors as Map<Field, string>;

export const fieldLabels: Map<Field, string> = new Map([
    ['awaiting_am_approval', 'Submits to AM'],
    ['awaiting_client_feedback', 'Client Submissions'],
    ['email_send_candidate', 'Candidate Email Followups'],
    ['email_send_client', 'Client Email Followups'],
    ['manual_sourcing', 'Manual Sourcing'],
    ['phone_calls_and_texts', 'Phone calls and Texts'],
    ['search_create', 'ML Searches'],
    ['outreach', 'Outreach'],
    ['response_received', 'Response Received'],
    ['rocket_screen_complete', 'Phone Screens'],
    ['initial_phone_call', 'Initial Phone Calls'],
    ['follow_up_phone_call', 'Follow Up Calls'],
    ['text_message', 'Text messages'],
    ['client_first_round', 'Client Accepts'],
    ['client_second_round', 'Final Rounds'],
    ['offer', 'Offers'],
    ['hired', 'Placements']
] as Array<[Field, string]>);

export const getFieldLabel = (field: Field): string => fieldLabels.get(field) ?? field;

export function getMetricsByClient(metrics: MetricsData[], variant: 'cost' | 'count') {
    return metrics?.reduce((acc, m) => {
        const index = acc.findIndex((v) => v.clientId === m.job.client.id);
        const getFieldMetric = (f: Field, includeKinds?: ActivityMetricType[]) => {
            const current = acc[index]?.metrics?.[f] ?? { value: 0, jobs: [] };
            if ((includeKinds && includeKinds.includes(m.kind)) || (!includeKinds && m.kind === f)) {
                const metric = { value: m[variant], jobs: [{ id: m.job.id, name: m.job.title, value: m[variant] }] };
                const jobIndex = current.jobs.findIndex((j) => j.id === m.job.id);
                const jobs =
                    jobIndex === -1
                        ? current.jobs.concat([{ id: m.job.id, name: m.job.title, value: m[variant] }])
                        : current.jobs.slice(0, jobIndex).concat(
                              [
                                  {
                                      id: m.job.id,
                                      name: m.job.title,
                                      value: m[variant] + current.jobs[jobIndex].value
                                  }
                              ],
                              current.jobs.slice(jobIndex + 1)
                          );
                return { value: current.value + metric.value, jobs };
            } else {
                return current;
            }
        };
        const clientData: ClientMetrics = {
            client: m.job.client.name,
            clientId: m.job.client.id,
            metrics: {
                awaiting_am_approval: getFieldMetric('awaiting_am_approval'),
                awaiting_client_feedback: getFieldMetric('awaiting_client_feedback'),
                client_first_round: getFieldMetric('client_first_round'),
                client_second_round: getFieldMetric('client_second_round'),
                email_send_candidate: getFieldMetric('email_send_candidate'),
                email_send_client: getFieldMetric('email_send_client'),
                follow_up_phone_call: getFieldMetric('follow_up_phone_call'),
                hired: getFieldMetric('hired'),
                initial_phone_call: getFieldMetric('initial_phone_call'),
                manual_sourcing: getFieldMetric('manual_sourcing'),
                offer: getFieldMetric('offer'),
                outreach: getFieldMetric('outreach'),
                response_received: getFieldMetric('response_received'),
                rocket_screen_complete: getFieldMetric('rocket_screen_complete'),
                search_create: getFieldMetric('search_create'),
                text_message: getFieldMetric('text_message')
            }
        };
        if (index !== -1) {
            acc[index] = clientData;
        } else {
            acc = acc.concat([clientData]);
        }
        return acc;
    }, [] as ClientMetrics[]);
}

export const customTooltip =
    (metrics: ClientMetrics[], fields: Field[], series: Array<{ data: number[]; name: string }>) =>
    ({ dataPointIndex, seriesIndex }: { dataPointIndex: number; seriesIndex: number }) => {
        const record = metrics[dataPointIndex];
        const field = fields[seriesIndex];
        const label = getFieldLabel(field);
        const total = series[seriesIndex].data.reduce((acc, val) => acc + val, 0);
        const percent = Math.round((series[seriesIndex].data[dataPointIndex] / (total || 1)) * 100);
        const jobs = record?.metrics?.[field]?.jobs
            ?.map(
                (j) => `
        <div class="metrics-tooltip-header">Job</div>
        <div class="metrics-tooltip-value">${j.name}: ${j.value}</div>
    `
            )
            .join('\n');
        return `
        <div class="metrics-tooltip">
            <div class="metrics-tooltip-header">Client</div>
            <div class="metrics-tooltip-value">${record?.client ?? ''}</div>
            <div class="metrics-tooltip-header">${label}</div>
            <div class="metrics-tooltip-value">${series[seriesIndex].data[dataPointIndex] ?? 0}</div>
            <div class="metrics-tooltip-header">Percent</div>
            <div class="metrics-tooltip-value">${percent}% of all ${label}</div>
            ${jobs ?? ''}
        </div>`;
    };

const escapeHtml = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface StackedTooltipConfig {
    entries: Array<{ label: string; metrics: Partial<Record<Field, number>> }>;
    fields: Field[];
    header: string;
    percentSuffix?: string;
    series: Array<{ data: number[]; name: string }>;
}

export const buildStackedTooltipFormatter =
    (config: StackedTooltipConfig) =>
    (raw: any): string => {
        const params = Array.isArray(raw) ? raw[0] : raw;
        const dataIndex = params?.dataIndex ?? 0;
        const seriesIndex = params?.seriesIndex ?? 0;
        const entry = config.entries[dataIndex];
        const field = config.fields[seriesIndex];
        if (!entry || !field) {
            return '';
        }
        const value = entry.metrics[field] ?? 0;
        const seriesData = config.series[seriesIndex]?.data ?? [];
        const total = seriesData.reduce((acc, val) => acc + (typeof val === 'number' ? val : 0), 0);
        const label = getFieldLabel(field);
        const percent = total ? Math.round((value / total) * 100) : 0;
        return `
            <div class="metrics-tooltip">
                <div class="metrics-tooltip-header">${escapeHtml(config.header)}</div>
                <div class="metrics-tooltip-value">${escapeHtml(entry.label)}</div>
                <div class="metrics-tooltip-header">${label}</div>
                <div class="metrics-tooltip-value">${value}</div>
                <div class="metrics-tooltip-header">Percent</div>
                <div class="metrics-tooltip-value">${percent}% of all ${label}${config.percentSuffix ?? ''}</div>
            </div>
        `;
    };

const fallbackFieldColor = 'rgba(38, 50, 56, 0.65)';
const fallbackMutedFieldColor = 'rgba(38, 50, 56, 0.12)';

const toMutedColor = (color: string) => {
    if (color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', ', 0.08)');
    }
    if (color.startsWith('rgba(')) {
        return color.replace(/rgba\((.+),\s*[\d.]+\)/, 'rgba($1, 0.08)');
    }
    return fallbackMutedFieldColor;
};

export const getFieldColors = (fields: Field[], hoveredField?: Field) => {
    return fields.map((f) => {
        const baseColor = fieldColors.get(f) ?? fallbackFieldColor;
        if (hoveredField && hoveredField !== f) {
            return toMutedColor(baseColor);
        }
        return baseColor;
    });
};

const getPeriodSize = (delta: number): 'day' | 'week' | 'month' => {
    // tslint:disable:no-magic-numbers
    return delta <= moment.duration(5, 'weeks').valueOf()
        ? 'day'
        : delta < moment.duration(14, 'months').valueOf()
          ? 'week'
          : 'month';
    // tslint:enable:no-magic-numbers
};

export type PeriodUnit = 'day' | 'week' | 'month';

export interface PeriodSpec {
    unit: PeriodUnit;
    count?: number;
}

export interface TimeSeriesSpec {
    tick: PeriodSpec;
    lookback?: PeriodSpec;
}

const normalizePeriodSpec = (spec: PeriodSpec): Required<PeriodSpec> => {
    const rawCount = spec.count ?? 1;
    const count = rawCount <= 0 ? 1 : rawCount;
    return { unit: spec.unit, count };
};

const normalizeLookbackSpec = (spec?: PeriodSpec): Required<PeriodSpec> | undefined => {
    if (!spec) {
        return undefined;
    }
    const count = spec.count ?? 1;
    if (count <= 0) {
        return undefined;
    }
    return { unit: spec.unit, count };
};

const getPeriodLabelPrefix = (spec: PeriodSpec) => {
    const normalized = normalizePeriodSpec(spec);
    const count = normalized.count;
    const unit = normalized.unit;

    const plural = count === 1 ? unit : `${unit}s`;
    return `Last ${count} ${plural}`;
};

export const getQueryRangeForSpec = (displayStart: number, displayEnd: number, spec?: TimeSeriesSpec) => {
    if (!spec) {
        return { start: displayStart, end: displayEnd };
    }

    const tick = normalizePeriodSpec(spec.tick);
    const lookback = normalizeLookbackSpec(spec.lookback);
    const window = lookback ?? tick;

    const start = moment(displayStart).startOf('day').subtract(window.count, window.unit).add(1, 'day').valueOf();
    return { start, end: displayEnd };
};

const getTickLabel = (tickEnd: moment.Moment): string => {
    const format = moment().isSame(tickEnd, 'year') ? 'MMM DD' : 'MMM DD, YYYY';
    return tickEnd.format(format);
};

const getPeriodsForSpec = (displayStart: number, displayEnd: number, spec: TimeSeriesSpec) => {
    const tick = normalizePeriodSpec(spec.tick);
    const lookback = normalizeLookbackSpec(spec.lookback);

    const tickEndDates: Array<{ tick: string; label: string; start: number; end: number }> = [];
    const minEnd = moment(displayStart).startOf('day');
    let tickEnd = moment(displayEnd).endOf('day');

    while (tickEnd.valueOf() >= minEnd.valueOf()) {
        const end = tickEnd.valueOf();
        const startMoment = lookback
            ? tickEnd.clone().subtract(lookback.count, lookback.unit).add(1, 'day').startOf('day')
            : tickEnd.clone().subtract(tick.count, tick.unit).add(1, 'day').startOf('day');
        const start = startMoment.valueOf();
        const tickLabel = getTickLabel(tickEnd);
        const label = `${chartDate(start, end)}`;
        tickEndDates.push({ tick: tickLabel, label, start, end });
        tickEnd = tickEnd.clone().subtract(tick.count, tick.unit);
    }

    tickEndDates.reverse();
    return tickEndDates;
};

const getPeriods = (startTs: number, endTs: number, periodSize?: 'day' | 'week' | 'month') => {
    const dates: Array<{ tick: string; label: string; start: number; end: number }> = [];

    const period = periodSize ?? getPeriodSize(endTs - startTs);
    let curr = moment(startTs).startOf(period).valueOf();
    do {
        const end = moment(curr).endOf(period).valueOf();
        const tick =
            period === 'day'
                ? moment(curr).format('MMM DD')
                : period === 'week'
                  ? moment().isSame(moment(curr), 'year')
                      ? moment((curr + end) / 2).format('MMM DD')
                      : moment((curr + end) / 2).format('MMM DD, YYYY')
                  : moment(curr).format('MMM YYYY');
        const label = `${chartDate(curr, end)}`;
        dates.push({ tick, label, start: curr, end });
        curr = moment(curr).add(1, period).startOf(period).valueOf();
        if (curr > endTs) {
            break;
        }
    } while (true);
    return dates;
};

export const metricsByTimeSeriesSpec = (
    metrics: Array<{ count: number; kind: ActivityMetricType; date: string }> | undefined,
    displayStart: number,
    displayEnd: number,
    spec: TimeSeriesSpec
) => {
    const source = metrics ?? [];
    const lookback = normalizeLookbackSpec(spec.lookback);
    const periods = getPeriodsForSpec(displayStart, displayEnd, spec);

    const result: Array<{
        period: { tick: string; label: string; start: number; end: number };
        metrics: FunnelCounts;
    }> = periods.map((period) => ({ period, metrics: {} }));

    // If lookback is set, each metric point may contribute to multiple windows.
    for (const metric of source) {
        const ts = moment(metric.date).startOf('day').valueOf();
        for (const bucket of result) {
            if (bucket.period.start <= ts && bucket.period.end >= ts) {
                bucket.metrics[metric.kind] = (bucket.metrics[metric.kind] ?? 0) + metric.count;
            }
        }
    }

    return {
        labelPrefix: lookback ? getPeriodLabelPrefix(lookback) : undefined,
        series: result
    };
};

export const metricsByPeriod = (
    metrics: Array<{ count: number; kind: ActivityMetricType; date: string }> | undefined,
    startTs: number,
    endTs: number,
    periodSize?: 'day' | 'week' | 'month'
) => {
    const source = metrics ?? [];
    const startTsSanitized =
        startTs === 0
            ? moment(source[0]?.date ?? Date.now())
                  .startOf('day')
                  .valueOf()
            : startTs;
    const result: Array<{
        period: { tick: string; label: string; start: number; end: number };
        metrics: FunnelCounts;
    }> = [];
    const periods = getPeriods(startTsSanitized, endTs, periodSize);
    for (const period of periods) {
        result.push({ period, metrics: {} });
    }

    for (const metric of source) {
        const ts = moment(metric.date).valueOf();
        const index = result.findIndex((p) => p.period.start <= ts && p.period.end >= ts);
        if (index !== -1) {
            result[index].metrics[metric.kind] = (result[index].metrics[metric.kind] ?? 0) + metric.count;
        }
    }
    return result;
};

export const funnelMetrics = (
    metrics: Array<{ count: number; cost: number; kind: ActivityMetricType }> | undefined,
    variant: 'cost' | 'count' = 'count'
) => {
    const source = metrics ?? [];
    return source.reduce(
        (acc, metric) => ({
            ...acc,
            [metric.kind]: (acc[metric.kind] ?? 0) + metric[variant]
        }),
        {} as FunnelCounts
    );
};
